'use client';

import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useSubscriptionVerification } from '../contexts';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import * as yup from 'yup';

import { PromoCodeForm } from '@/app/actions/promo-code/types';
import PromoSVG from '@/icons/PromoSVG';

import { useVerification } from '@/contexts/VerificationContext/useVerification';
import { useUsersSubscriptions } from '@/views/UserProfileViews/UserSubscriptionsView/PurchasingSubscriptionsView/useUsersSubscriptions';

import { UserApi } from '@/api/domains/user-api';

import { Form } from '@/components/Form';

import { useNotification } from '@/contexts/NotificationContext';
import styles from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/Payments/PromoCodeInput.module.scss';

interface PromoCodeInputProps {
  handleOnSuccessFn?: () => void;
}

// =================================================================

const PROMOCODE_LENGTH = 8;

// =================================================================

export const PromoCodeInput = (props: PromoCodeInputProps) => {
  const { handleOnSuccessFn } = props;
  const t = useTranslations();

  const { refetch } = useUsersSubscriptions();
  const { notification } = useNotification();

  const { checkAuthVerification } = useVerification();

  const { setPromoCodeResponse } = useSubscriptionVerification();

  const [pending, setPending] = useState(false);

  const handleSubmitWithCheckAuth = checkAuthVerification<{ data: PromoCodeForm }>({
    onSuccess: async params => {
      const { data: dataForm } = params!;
      setPending(true);
      try {
        const { message, data } = await UserApi.addPromoCode(dataForm.code);

        setPromoCodeResponse(data?.promo_code);
        refetch();
        notification('success', message);
        if (typeof handleOnSuccessFn === 'function') handleOnSuccessFn();
      } catch (error) {
        if (!error.response) {
          throw error;
        }

        const { message } = error.response;
        notification('error', message);
      } finally {
        setPending(false);
      }
    },
  });

  const schema = useMemo(
    () =>
      yup
        .object({
          code: yup.string().trim().required(t('formWarningMassage.requiredField')),
        })
        .required(),
    [t],
  );

  const methods = useForm<PromoCodeForm>({
    defaultValues: {
      code: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'code' && value.code?.length === PROMOCODE_LENGTH && !pending) {
        // Prevent submission if already pending
        handleSubmitWithCheckAuth({ data: { code: value.code } });
      }
    });

    return () => subscription.unsubscribe();
  }, [handleSubmitWithCheckAuth, methods, pending]);

  return (
    <FormProvider {...methods}>
      <Form isSubmitting={methods.formState.isSubmitting || pending} className="m-0">
        <Form.Body>
          <Form.Input
            name="code"
            icon={PromoSVG}
            placeholder={t('page.userProfile.placeholderPromoCode')}
            errorClassName={styles.errorMessage}
            className={clsx(styles.input, {
              [styles.disabled]: pending,
            })}
            maxLength={PROMOCODE_LENGTH}
            disabled={pending}
          />
        </Form.Body>
      </Form>
    </FormProvider>
  );
};
